import React from 'react';
import { Container } from '../ui/Container';
import { Button } from '../ui/Button';
import { ArrowRight, CreditCard, PenTool, Upload, HelpCircle, Star, Check, Truck } from 'lucide-react';
export function HeroSection() {
  const quickRoutes = [{
    icon: CreditCard,
    label: 'Business Cards',
    href: '#business-cards',
    color: 'text-cyan-600',
    bg: 'bg-cyan-50 group-hover:bg-cyan-100',
    border: 'group-hover:border-cyan-300'
  }, {
    icon: PenTool,
    label: 'Signage + Install',
    href: '#signage',
    color: 'text-magenta-600',
    bg: 'bg-magenta-50 group-hover:bg-magenta-100',
    border: 'group-hover:border-magenta-300'
  }, {
    icon: Upload,
    label: 'I Have Artwork',
    href: '#quote',
    color: 'text-blue-600',
    bg: 'bg-blue-50 group-hover:bg-blue-100',
    border: 'group-hover:border-blue-300'
  }, {
    icon: HelpCircle,
    label: 'Need Design Help?',
    href: '#design',
    color: 'text-yellow-600',
    bg: 'bg-yellow-50 group-hover:bg-yellow-100',
    border: 'group-hover:border-yellow-300'
  }];
  return <section className="relative bg-gradient-to-b from-white via-cyan-50/30 to-white pt-12 pb-20 lg:pt-20 lg:pb-28 overflow-hidden" data-id="element-240">
      {/* Abstract Background Elements */}
      <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-cyan-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" data-id="element-241"></div>
      <div className="absolute top-0 left-0 -ml-20 -mt-20 w-96 h-96 bg-magenta-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" data-id="element-242"></div>
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-yellow-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000" data-id="element-243"></div>

      {/* Dot Pattern Overlay */}
      <div className="absolute inset-0 bg-dots pointer-events-none" data-id="element-244"></div>

      <Container className="relative z-10" data-id="element-245">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16" data-id="element-246">
          {/* Left Content */}
          <div className="flex-1 text-center lg:text-left" data-id="element-247">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-cyan-100 text-cyan-800 text-xs font-bold tracking-wide uppercase mb-6 animate-fade-in-up" data-id="element-248">
              <span className="w-2 h-2 rounded-full bg-cyan-500 mr-2 animate-pulse" data-id="element-249"></span>
              Printing Made Personal
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 tracking-tight mb-6 leading-tight animate-fade-in-up" style={{
            animationDelay: '100ms'
          }} data-id="element-250">
              Professional printing & signage —{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 via-magenta-500 to-yellow-500" data-id="element-251">
                delivered Australia-wide
              </span>
            </h1>

            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed animate-fade-in-up" style={{
            animationDelay: '200ms'
          }} data-id="element-252">
              Let our team help you make a lasting impression. Business cards,
              signage, marketing print and more.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8 animate-fade-in-up" style={{
            animationDelay: '300ms'
          }} data-id="element-253">
              <Button size="lg" className="shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all transform hover:-translate-y-1" onClick={() => document.getElementById('quote')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-254">
                Request a Quote
                <ArrowRight className="ml-2 h-5 w-5" data-id="element-255" />
              </Button>
              <Button variant="outline" size="lg" className="border-2 hover:bg-gray-50" onClick={() => document.getElementById('services')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-256">
                Explore Services
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm font-medium text-gray-500 animate-fade-in-up" style={{
            animationDelay: '400ms'
          }} data-id="element-257">
              <div className="flex items-center gap-1" data-id="element-258">
                <div className="flex text-yellow-400" data-id="element-259">
                  <Star className="h-4 w-4 fill-current" data-id="element-260" />
                  <Star className="h-4 w-4 fill-current" data-id="element-261" />
                  <Star className="h-4 w-4 fill-current" data-id="element-262" />
                  <Star className="h-4 w-4 fill-current" data-id="element-263" />
                  <Star className="h-4 w-4 fill-current" data-id="element-264" />
                </div>
                <span className="text-gray-700" data-id="element-265">4.9/5 Rating</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-266">
                <Check className="h-4 w-4 text-green-500" data-id="element-267" />
                <span className="text-gray-700" data-id="element-268">Free Quotes</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-269">
                <Truck className="h-4 w-4 text-cyan-500" data-id="element-270" />
                <span className="text-gray-700" data-id="element-271">Australia-Wide</span>
              </div>
            </div>
          </div>

          {/* Right Image / Mascot Team */}
          <div className="flex-1 relative animate-fade-in-up" style={{
          animationDelay: '200ms'
        }} data-id="element-272">
            <div className="relative w-full max-w-lg mx-auto lg:max-w-full" data-id="element-273">
              {/* Floating blobs behind mascots */}
              <div className="absolute top-10 right-10 w-24 h-24 bg-cyan-400 rounded-full mix-blend-multiply filter blur-xl opacity-60 animate-float" style={{
              animationDelay: '0s'
            }} data-id="element-274"></div>
              <div className="absolute bottom-10 left-10 w-32 h-32 bg-magenta-400 rounded-full mix-blend-multiply filter blur-xl opacity-60 animate-float" style={{
              animationDelay: '1s'
            }} data-id="element-275"></div>
              <div className="absolute top-1/2 left-1/2 w-40 h-40 bg-yellow-400 rounded-full mix-blend-multiply filter blur-xl opacity-60 animate-float" style={{
              animationDelay: '2s'
            }} data-id="element-276"></div>

              {/* "Meet the team" label */}
              <p className="relative z-10 text-center text-sm font-bold text-gray-500 uppercase tracking-widest mb-3" data-id="element-277">
                Meet the team… <span className="text-cyan-600" data-id="element-278">Carl</span>,{' '}
                <span className="text-pink-500" data-id="element-279">Bob</span> &{' '}
                <span className="text-yellow-500" data-id="element-280">Dave</span>
              </p>

              {/* Mascot Team Image */}
              <img src="https://cdn.magicpatterns.com/uploads/3bcqf4k6a9sg46hUAHbxoH/Mascot_names.png" alt="Meet the Digital Printing team mascots - Carl, Bob and Dave" className="relative z-10 w-full h-auto drop-shadow-2xl" data-id="element-281" />
            </div>
          </div>
        </div>

        {/* Quick Route Tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto mt-20 animate-fade-in-up" style={{
        animationDelay: '500ms'
      }} data-id="element-282">
          {quickRoutes.map(route => <a key={route.label} href={route.href} className={`flex flex-col items-center justify-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 group ${route.border}`} data-id="element-283">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 transition-colors ${route.bg}`} data-id="element-284">
                <route.icon className={`h-7 w-7 transition-colors ${route.color}`} data-id="element-285" />
              </div>
              <span className="font-bold text-gray-900 text-center text-sm group-hover:text-cyan-600 transition-colors" data-id="element-286">
                {route.label}
              </span>
            </a>)}
        </div>
      </Container>
    </section>;
}